import React, { forwardRef, Ref, useContext, useImperativeHandle, useState } from 'react';
import { AxiosPromise } from 'axios';
import { ErrorModal, LoadingModal, NameNewModal, SavedModal } from '../Modal';
import { getRedirectLink } from '../BottomNavigation/RedirectLink';
import { LaterExemplar, RelatedType } from '../../types';
import { UndoRedoContext } from '../../contexts/undoRedoContext';

interface SavedExemplar extends LaterExemplar {
  id: number;
  page: string;
}

interface ISaveTool {
  color: {
    main: string;
    light: string;
  };
  showSaveModal: boolean;
  closeSaveModal: () => void;
  save: (textName: string) => AxiosPromise;
}

type SaveToolHandle = {
  forceSave: (saveFunc: () => AxiosPromise, isUpdating?: boolean) => Promise<void>;
};

export const SaveTool = forwardRef((props: ISaveTool, ref: Ref<SaveToolHandle>) => {
  const { color, showSaveModal, closeSaveModal, save } = props;

  const [textName, setTextName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [savedExemplar, setSavedExemplar] = useState<SavedExemplar | null>(null);

  const { clearEvents } = useContext(UndoRedoContext);

  useImperativeHandle(ref, () => ({
    forceSave: (saveFunc: () => AxiosPromise, isUpdating?: boolean) => handleSave(saveFunc, isUpdating),
  }));

  const handleSave = async (saveFunc?: () => AxiosPromise, isUpdating?: boolean) => {
    setIsSaving(true);
    setIsError(false);
    setIsSaved(false);
    setIsUpdated(false);

    try {
      const { data } = await (typeof saveFunc === 'function' ? saveFunc : save)(textName);
      setSavedExemplar({ ...data.data.content, id: data.data.id });
      setIsSaved(true);
      isUpdating && setIsUpdated(true);
      clearEvents();
    } catch (error) {
      setIsError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const closeErrorModal = () => {
    setIsError(false);
    closeSaveModal();
  };

  if (isSaving) {
    return <LoadingModal color={color.main} headerText="Saving..." />;
  }

  if (isError) {
    return (
      <ErrorModal
        title={textName}
        mainColor={color.main}
        highlightColor={color.light}
        errorType="save"
        onRetryClick={handleSave}
        onCloseClick={closeErrorModal}
      />
    );
  }

  if (isSaved && savedExemplar) {
    const link = getRedirectLink(
      savedExemplar.stage,
      savedExemplar.text_type,
      RelatedType.saved_exemplars,
      savedExemplar.id,
      false,
      savedExemplar.page
    );

    return (
      <SavedModal
        name={savedExemplar.title}
        textType={savedExemplar.text_type}
        stage={savedExemplar.stage}
        mainColor={color.main}
        highlightColor={color.light}
        isUpdated={isUpdated}
        onClose={() => window.location.reload()}
        onConfirm={() => window.location.assign(link)}
      />
    );
  }

  return showSaveModal ? (
    <NameNewModal
      mainColor={color.main}
      headerText="Give your new text a title"
      value={textName}
      setValue={setTextName}
      onButtonClick={() => handleSave()}
      onCloseClick={closeSaveModal}
    />
  ) : null;
});
